import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { documentsAPI, jobsAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
  FileWarning, FileCheck, ChevronLeft, ChevronRight, CheckCircle,
  XCircle, Edit3, Save, AlertTriangle, RefreshCw, RotateCcw, Loader2, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const ReviewQueuePage = () => {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [filters, setFilters] = useState({ module_id: '', document_type: '' });
  const [showFilters, setShowFilters] = useState(false);

  const tenantId = user?.tenant_id || 'default';

  const loadQueue = useCallback(async () => {
    try {
      const params = { tenant_id: tenantId };
      if (filters.module_id) params.module_id = filters.module_id;
      const res = await documentsAPI.getReviewQueue(params);
      let items = res.data;
      if (filters.document_type) items = items.filter(i => i.document?.document_type === filters.document_type);
      setQueue(items);
      if (items.length > 0 && currentIndex >= items.length) setCurrentIndex(0);
      if (items.length > 0) setEditedFields(items[Math.min(currentIndex, items.length - 1)]?.extracted_data?.structured_fields || {});
    } catch { toast.error('Failed to load review queue'); }
    finally { setLoading(false); }
  }, [tenantId, filters, currentIndex]);

  useEffect(() => { loadQueue(); }, [loadQueue]);
  useEffect(() => { documentsAPI.listTemplates().then(r => setTemplates(r.data)).catch(() => {}); }, []);

  const currentItem = queue[currentIndex];

  const handleNavigate = (dir) => {
    const idx = currentIndex + dir;
    if (idx >= 0 && idx < queue.length) {
      setCurrentIndex(idx);
      setEditedFields(queue[idx]?.extracted_data?.structured_fields || {});
      setReviewNotes('');
      setEditMode(false);
    }
  };

  const handleAction = async (approve) => {
    if (!currentItem) return;
    if (!approve && !reviewNotes.trim()) { toast.error('Add notes for rejection'); return; }
    setSubmitting(true);
    try {
      await documentsAPI.submitReview(currentItem.document.id, {
        document_id: currentItem.document.id, extracted_data_id: currentItem.extracted_data?.id || '',
        corrected_fields: editedFields, is_approved: approve, review_notes: reviewNotes,
      });
      toast.success(approve ? 'Document approved' : 'Document rejected');
      const newQueue = queue.filter((_, i) => i !== currentIndex);
      setQueue(newQueue);
      if (newQueue.length > 0) {
        const ni = Math.min(currentIndex, newQueue.length - 1);
        setCurrentIndex(ni);
        setEditedFields(newQueue[ni]?.extracted_data?.structured_fields || {});
        setReviewNotes('');
      }
    } catch { toast.error('Review submission failed'); }
    finally { setSubmitting(false); }
  };

  const handleReprocess = async (template) => {
    if (!currentItem) return;
    setReprocessing(true);
    try {
      await documentsAPI.reprocess(currentItem.document.id, template || undefined);
      toast.success('Document queued for reprocessing');
      const newQueue = queue.filter((_, i) => i !== currentIndex);
      setQueue(newQueue);
      if (newQueue.length > 0) {
        const ni = Math.min(currentIndex, newQueue.length - 1);
        setCurrentIndex(ni);
        setEditedFields(newQueue[ni]?.extracted_data?.structured_fields || {});
      }
    } catch { toast.error('Reprocess failed'); }
    finally { setReprocessing(false); }
  };

  const confColor = (s) => s >= 0.85 ? 'text-emerald-600 bg-emerald-50' : s >= 0.7 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-2 gap-6"><Skeleton className="h-[600px]" /><Skeleton className="h-[600px]" /></div></div>;

  if (queue.length === 0) return (
    <div className="space-y-6" data-testid="review-queue-page">
      <div className="flex items-center justify-between"><div><h1 className="text-3xl font-semibold text-slate-900">Review Queue</h1><p className="text-slate-500 mt-1">Review and verify OCR results</p></div>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)}><Filter className="mr-2 h-4 w-4" />Filters</Button></div>
      {showFilters && <FilterBar filters={filters} setFilters={setFilters} />}
      <Card className="py-16"><CardContent className="text-center"><FileCheck className="h-16 w-16 text-emerald-500 mx-auto mb-4" /><h3 className="text-xl font-medium text-slate-900">All caught up!</h3><p className="text-slate-500 mt-2">No documents pending review</p><Button variant="outline" className="mt-4" onClick={loadQueue}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button></CardContent></Card>
    </div>
  );

  const confScores = currentItem?.extracted_data?.confidence_scores || {};
  const overallConf = currentItem?.extracted_data?.overall_confidence || 0;

  return (
    <div className="space-y-6" data-testid="review-queue-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-semibold text-slate-900">Review Queue</h1><p className="text-slate-500 mt-1">Reviewing {currentIndex + 1} of {queue.length}</p></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}><Filter className="mr-2 h-4 w-4" />Filters</Button>
          <Button variant="outline" size="icon" onClick={() => handleNavigate(-1)} disabled={currentIndex === 0} data-testid="prev-doc-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-slate-500 px-2">{currentIndex + 1}/{queue.length}</span>
          <Button variant="outline" size="icon" onClick={() => handleNavigate(1)} disabled={currentIndex >= queue.length - 1} data-testid="next-doc-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {showFilters && <FilterBar filters={filters} setFilters={setFilters} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Preview */}
        <Card className="h-[700px] flex flex-col">
          <CardHeader className="border-b"><CardTitle className="text-lg">Document Preview</CardTitle><CardDescription className="truncate">{currentItem?.document?.original_filename}</CardDescription></CardHeader>
          <CardContent className="flex-1 p-0 overflow-auto bg-slate-100">
            {currentItem?.document?.mime_type?.startsWith('image/') ? (
              <div className="p-2 flex justify-center"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${currentItem.document.id}/file`} alt="Document" className="max-w-full max-h-[350px] object-contain rounded" onError={(e) => { e.target.style.display='none'; }} /></div>
            ) : (
              <div className="p-4 text-center"><FileWarning className="h-12 w-12 text-slate-400 mx-auto mb-4" /><p className="text-slate-500">Preview not available</p><p className="text-sm text-slate-400 mt-1">{currentItem?.document?.mime_type} / {((currentItem?.document?.file_size || 0) / 1024).toFixed(1)} KB</p></div>
            )}
            {currentItem?.extracted_data?.raw_text && (
              <div className="p-4 bg-white border-t"><h4 className="text-sm font-medium text-slate-700 mb-2">Raw OCR Text</h4><pre className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg max-h-[250px] overflow-y-auto">{currentItem.extracted_data.raw_text}</pre></div>
            )}
          </CardContent>
        </Card>

        {/* Right: Extracted Data */}
        <Card className="h-[700px] flex flex-col">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div><CardTitle className="text-lg">Extracted Data</CardTitle><CardDescription>Confidence: <span className={`font-mono px-2 py-0.5 rounded ${confColor(overallConf)}`}>{(overallConf * 100).toFixed(0)}%</span></CardDescription></div>
              <div className="flex gap-2">
                <Button variant={editMode ? 'default' : 'outline'} size="sm" onClick={() => setEditMode(!editMode)} data-testid="toggle-edit-btn">
                  {editMode ? <><Save className="mr-2 h-4 w-4" />Done</> : <><Edit3 className="mr-2 h-4 w-4" />Edit</>}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleReprocess()} disabled={reprocessing} data-testid="reprocess-btn">
                  {reprocessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto p-6">
            <div className="space-y-4">
              {Object.entries(editedFields).map(([key, value]) => {
                const conf = confScores[key] || 0;
                const low = conf < 0.7;
                return (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center justify-between"><Label className="capitalize">{key.replace(/_/g, ' ')}</Label><span className={`text-xs font-mono px-2 py-0.5 rounded ${confColor(conf)}`}>{(conf * 100).toFixed(0)}%</span></div>
                    {editMode ? (
                      <Input value={value || ''} onChange={e => setEditedFields(p => ({...p, [key]: e.target.value}))} className={low ? 'border-amber-300 bg-amber-50' : ''} data-testid={`field-${key}`} />
                    ) : (
                      <div className={`p-3 rounded-lg border ${low ? 'border-amber-300 bg-amber-50' : 'bg-slate-50'}`}>
                        <span className="text-sm">{value || <span className="text-slate-400 italic">Not detected</span>}</span>
                        {low && <AlertTriangle className="inline-block ml-2 h-4 w-4 text-amber-500" />}
                      </div>
                    )}
                  </div>
                );
              })}
              {Object.keys(editedFields).length === 0 && <p className="text-slate-500 text-center py-8">No structured data extracted</p>}
            </div>

            {/* Rerun with template */}
            {templates.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <Label className="mb-2 block text-sm">Rerun with Template</Label>
                <div className="flex gap-2 flex-wrap">
                  {templates.map(t => (
                    <Button key={t.key} variant="outline" size="sm" onClick={() => handleReprocess(t.key)} disabled={reprocessing}>
                      {t.key}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 pt-6 border-t"><Label className="mb-2 block">Review Notes</Label><Textarea placeholder="Notes (required for rejection)..." value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} className="min-h-[80px]" data-testid="review-notes" /></div>
          </CardContent>
          <div className="border-t p-4 flex items-center justify-end gap-3">
            <Button variant="outline" onClick={() => handleAction(false)} disabled={submitting} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="reject-btn"><XCircle className="mr-2 h-4 w-4" />Reject</Button>
            <Button onClick={() => handleAction(true)} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn"><CheckCircle className="mr-2 h-4 w-4" />Approve</Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

const FilterBar = ({ filters, setFilters }) => (
  <Card className="p-4">
    <div className="flex gap-4 flex-wrap items-end">
      <div className="space-y-1">
        <Label className="text-xs">Module</Label>
        <Select value={filters.module_id || 'all_modules'} onValueChange={v => setFilters(p => ({...p, module_id: v === 'all_modules' ? '' : v}))}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all_modules">All Modules</SelectItem><SelectItem value="timesheet_ocr">Timesheet OCR</SelectItem><SelectItem value="accounting_intake">Accounting Intake</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Document Type</Label>
        <Select value={filters.document_type || 'all_types'} onValueChange={v => setFilters(p => ({...p, document_type: v === 'all_types' ? '' : v}))}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all_types">All Types</SelectItem><SelectItem value="timesheet">Timesheet</SelectItem><SelectItem value="receipt">Receipt</SelectItem><SelectItem value="invoice">Invoice</SelectItem><SelectItem value="contract">Contract</SelectItem></SelectContent>
        </Select>
      </div>
    </div>
  </Card>
);

export default ReviewQueuePage;
